import { useState, useEffect } from 'react';
import {
  useSelector,
  shallowEqual,
  useDispatch,
  type TypedUseSelectorHook,
} from 'react-redux';
import jwtDecode from 'jwt-decode';
import { useIntl, defineMessages } from 'react-intl';
import cx from 'classnames';

import { getUser } from '@plone/volto/actions/users/users';
import config from '@plone/registry';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import clockSVG from '@plone/volto/icons/clock.svg';
import deleteSVG from '@plone/volto/icons/delete.svg';
import styles from '@redturtle/qubicaamf-theme/components/View/FormView/Analytics/styles.module.css';

interface RootState {
  userSession: { token: string | null };
  users: {
    user: {
      roles: string[];
    };
  };
}

const Field = ({ label, onChange, value, max, name }) => {
  return (
    <div className={styles.field}>
      <div className={styles.date}>
        <label htmlFor={name}>{label}</label>
        <input
          type="date"
          onChange={(e) => onChange(e.target.value)}
          name={name}
          id={name}
          value={value}
          max={max}
        />
      </div>
      <button
        onClick={() => onChange(max)}
        className={cx(styles.todayButton, styles.actionButton)}
        aria-label="set to today"
        title="set to today"
        type="button"
      >
        <Icon name={clockSVG} size="20px" />
      </button>
      <button
        onClick={() => onChange('')}
        className={cx(styles.deleteButton, styles.actionButton)}
        type="button"
      >
        <Icon name={deleteSVG} size="20px" />
      </button>
    </div>
  );
};

const FormAnalytics = (props) => {
  const dispatch = useDispatch();

  const useTypedSelector: TypedUseSelectorHook<RootState> =
    useSelector<RootState>;

  const token = useTypedSelector(
    (state) => state?.userSession?.token,
    shallowEqual,
  );
  const userId = token ? jwtDecode(token).sub : '';

  useEffect(() => {
    dispatch(getUser(userId));
  }, [dispatch, userId]);

  const user = useTypedSelector((state) => state?.users?.user);
  const intl = useIntl();
  const Container = config.getComponent('Container').component || 'div';
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const today = new Date();
  const todayString = `${today.getFullYear()}-${(today.getMonth() + 1)
    .toString()
    .padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`;

  return token && user?.roles?.includes('Manager') ? (
    <div className="ui public-ui">
      <Container className="container">
        <h1>Form Analytics</h1>
        <div>{intl.formatMessage(messages.form_analytics_description)}</div>
        <div className={styles.dates}>
          <Field
            label={intl.formatMessage(messages.form_analytics_from_date_label)}
            onChange={setStartDate}
            value={startDate}
            max={todayString}
            name={'startDate'}
          />
          <Field
            label={intl.formatMessage(messages.form_analytics_to_date_label)}
            onChange={setEndDate}
            value={endDate}
            max={todayString}
            name={'endDate'}
          />

          <a
            href={`/@@download/contact-analytics/${startDate}/${endDate}`}
            className={cx(styles.button, {
              [styles.disabled]: !startDate || !endDate,
            })}
            onClick={(e) => {
              if (!startDate || !endDate) {
                e.preventDefault();
              }
            }}
          >
            Download
          </a>
        </div>
      </Container>
    </div>
  ) : (
    <Unauthorized />
  );
};

export default FormAnalytics;

const messages = defineMessages({
  form_analytics_description: {
    id: 'form_analytics_description',
    defaultMessage: 'Download information about contact form submissions',
  },
  form_analytics_from_date_label: {
    id: 'form_analytics_from_date_label',
    defaultMessage: 'From date',
  },
  form_analytics_to_date_label: {
    id: 'form_analytics_to_date_label',
    defaultMessage: 'To date',
  },
});
